import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {openErrorModal} from '@Modals/error-modal/error-modal';
import {executeThunk} from '@Utils/thunk.util';
import {getStockFavoritesLoadStatus} from '@Panels/stock-panel/stock-favorites-reducer';
import {getStockFavoriteToggleErrorMessage} from '@Panels/stock-panel/stock-favorites.library';
import {StockFavoritesLoadStatus} from '@Panels/stock-panel/stock-favorites.types';
import {useAppSelector} from '@/hooks';
import type {FavoriteHeart, FavoriteHeartParams, FavoriteToggleRejection} from './favorite-button.types';
import {FAVORITED_HEART_ICON, UNFAVORITED_HEART_ICON} from './favorite-button.types';

/**
 * What every favorites heart shares: its icon, its accessible label and its tap, which reports a tap that adds to
 * GA4, with how many items it adds, before the server answers. Null until the favorites have
 * loaded, because before then a heart cannot tell a favorite from a stranger and a tap would be overwritten by the load.
 * A tap while a toggle is in flight is ignored rather than disabling the icon: the disabled look would paint over the
 * heart's optimistic red until the request returns.
 */
export const useFavoriteHeart = ({isFavorited, isTogglePending, addedItemCount, requestToggle}: FavoriteHeartParams): FavoriteHeart | null => {
  const areFavoritesLoaded = useAppSelector((state) => getStockFavoritesLoadStatus(state) === StockFavoritesLoadStatus.LOADED);
  if (!areFavoritesLoaded) {
    return null;
  }

  const toggleHeart = (): void => {
    if (isTogglePending) {
      return;
    }
    if (!isFavorited) {
      trackPosterBuilderGA4Events(GA4EventName.MEDIA_MODAL_FAVOURITE_ITEM, {[GA4EventParamName.QUANTITY]: String(addedItemCount)});
    }
    void executeThunk(requestToggle, undefined, showToggleError);
  };

  return {
    heartIcon: isFavorited ? FAVORITED_HEART_ICON : UNFAVORITED_HEART_ICON,
    accessibleLabel: isFavorited ? window.i18next.t('pmwjs_remove_from_favorites') : window.i18next.t('pmwjs_add_to_favorites'),
    toggleHeart,
  };
};

const showToggleError = (rejection: FavoriteToggleRejection): void => {
  openErrorModal({message: getStockFavoriteToggleErrorMessage(rejection.payload)});
};
